const STORAGE_PREFIX = 'oscar:mission-pattern-handoff:';
const MAX_HANDOFF_AGE_MS = 24 * 60 * 60 * 1000;

export type MissionPatternHandoffPoint = {
    pointIndex: number;
    label?: string | null;
    gpsLat?: number | null;
    gpsLon?: number | null;
    gpsAlt?: number | null;
    xLocal?: number | null;
    yLocal?: number | null;
};

export type MissionPatternHandoffResult =
    | { status: 'cancelled' }
    | { status: 'saved'; patternId: number }
    | {
        status: 'mission-only';
        coordSystem: 'gps' | 'local';
        definition?: Record<string, any> | null;
        points: MissionPatternHandoffPoint[];
    };

export type MissionPatternHandoff = {
    version: 1;
    createdAt: string;
    companyId?: number | null;
    draft: {
        formValues: Record<string, any>;
        draftPoints: any[];
        draftCommands: any[];
        confirmedAreaId: number | null;
        confirmedDeviceId: number | null;
        moduleStepConfirmed: boolean;
        oscarModuleExecutionMode: string;
        oscarModuleSettings: Record<string, any>;
        confirmedSlamMapIds: number[];
        slamMapStepConfirmed: boolean;
        patternGuidedDirections: boolean;
        obstaclePolicyMode: string;
        costMode: string;
        sweepAngles: number;
        windowSize: number;
    };
    result?: MissionPatternHandoffResult;
};

const storageKey = (draftKey: string) => `${STORAGE_PREFIX}${draftKey}`;

const canUseSessionStorage = () => typeof window !== 'undefined' && Boolean(window.sessionStorage);

export const createMissionPatternDraftKey = (): string => {
    const randomUUID = typeof crypto !== 'undefined' ? (crypto as any).randomUUID : null;
    if (typeof randomUUID === 'function') {
        return randomUUID.call(crypto);
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const writeMissionPatternHandoff = (draftKey: string, handoff: MissionPatternHandoff): boolean => {
    if (!canUseSessionStorage()) return false;
    try {
        window.sessionStorage.setItem(storageKey(draftKey), JSON.stringify(handoff));
        return true;
    } catch (error) {
        console.error('Failed to preserve the mission draft for pattern creation', error);
        return false;
    }
};

export const readMissionPatternHandoff = (draftKey?: string | null): MissionPatternHandoff | null => {
    if (!draftKey || !canUseSessionStorage()) return null;
    try {
        const raw = window.sessionStorage.getItem(storageKey(draftKey));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as MissionPatternHandoff;
        const createdAt = Date.parse(parsed?.createdAt || '');
        if (
            parsed?.version !== 1
            || !parsed.draft
            || !Number.isFinite(createdAt)
            || Date.now() - createdAt > MAX_HANDOFF_AGE_MS
        ) {
            window.sessionStorage.removeItem(storageKey(draftKey));
            return null;
        }
        return parsed;
    } catch (error) {
        console.error('Failed to restore the mission draft after pattern creation', error);
        window.sessionStorage.removeItem(storageKey(draftKey));
        return null;
    }
};

export const writeMissionPatternResult = (
    draftKey: string,
    result: MissionPatternHandoffResult
): boolean => {
    const handoff = readMissionPatternHandoff(draftKey);
    if (!handoff) return false;
    return writeMissionPatternHandoff(draftKey, { ...handoff, result });
};

export const clearMissionPatternHandoff = (draftKey?: string | null) => {
    if (!draftKey || !canUseSessionStorage()) return;
    window.sessionStorage.removeItem(storageKey(draftKey));
};

export const buildMissionPatternReturnUrl = (draftKey: string) => (
    `/operations?resumeMissionDraft=${encodeURIComponent(draftKey)}`
);
